import { apiClient } from './client';
import type {
  LetterTaskResponse,
  LetterSseEvent,
  LetterRecord,
  BatchLetterConfig,
  BatchProgress,
} from '@/types/letters';

export const LettersApi = {
  async listLetters(caseId: string): Promise<LetterRecord[]> {
    const res = await apiClient.get<any>(
      `/letters?case_id=${encodeURIComponent(caseId)}`
    );
    // Normalize various possible response shapes
    if (Array.isArray(res)) return res as LetterRecord[];
    if (res?.data && Array.isArray(res.data)) return res.data as LetterRecord[];
    if (res?.items && Array.isArray(res.items))
      return res.items as LetterRecord[];
    // Some backends return { success, letters }
    if (res?.letters && Array.isArray(res.letters))
      return res.letters as LetterRecord[];
    return [];
  },

  async deleteLetter(letterId: string): Promise<{ success?: boolean }> {
    const res = await apiClient.delete<any>(
      `/letters/${encodeURIComponent(letterId)}`
    );
    return res;
  },

  async getLetter(letterId: string): Promise<LetterRecord | null> {
    const res = await apiClient.get<any>(
      `/letters/${encodeURIComponent(letterId)}`
    );
    if (!res) return null;
    if (res?.data) return res.data as LetterRecord;
    return res as LetterRecord;
  },

  async createLetter(payload: {
    caseId: string;
    letter_type: string;
    file_ids?: string[];
    referee_name?: string;
    recommender_background?: string;
    recommender_document_id?: string;
    relationship_to_beneficiary?: string;
    achievements_to_highlight?: string;
    selected_criteria?: string[];
    selected_sections?: string[];
    whitelist?: string;
    blacklist?: string;
    target_word_count?: number;
    recommender_is_us_based?: boolean;
    recommender_country?: string;
    metadata?: Record<string, any>;
  }): Promise<LetterTaskResponse> {
    const res = await apiClient.post<LetterTaskResponse>(`/letters`, {
      caseId: payload.caseId,
      letter_type: payload.letter_type,
      file_ids: payload.file_ids || [],
      referee_name: payload.referee_name,
      recommender_background: payload.recommender_background,
      recommender_document_id: payload.recommender_document_id,
      relationship_to_beneficiary: payload.relationship_to_beneficiary,
      achievements_to_highlight: payload.achievements_to_highlight,
      selected_criteria: payload.selected_criteria,
      selected_sections: payload.selected_sections,
      whitelist: payload.whitelist,
      blacklist: payload.blacklist,
      target_word_count: payload.target_word_count,
      recommender_is_us_based: payload.recommender_is_us_based,
      recommender_country: payload.recommender_country,
      metadata: payload.metadata,
    });
    return res;
  },

  // Poll task status instead of SSE for better reliability
  streamTask(
    taskId: string,
    onEvent: (evt: LetterSseEvent) => void,
    onError?: (e: Event) => void
  ): () => void {
    let isActive = true;
    let intervalId: NodeJS.Timeout;

    const pollTaskStatus = async () => {
      if (!isActive) return;

      try {
        // Add cache-busting timestamp and headers to prevent stale responses
        const timestamp = Date.now();
        const response = await fetch(
          `${((import.meta as any).env?.VITE_API_URL || 'http://localhost:3001').replace(/\/$/, '')}/api/letters/tasks/${encodeURIComponent(taskId)}?_t=${timestamp}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              Pragma: 'no-cache',
              Expires: '0',
            },
          }
        ).then(res => res.json());

        if (!isActive) return;

        const isCompleted =
          response.status === 'completed' ||
          response.status === 'done' ||
          response.status === 'ready' ||
          response.progress === 100;

        const eventData: LetterSseEvent = {
          taskId,
          progress: response.progress || 0,
          message: response.message || `${response.status || 'processing'}`,
          status: response.status || 'processing',
          isFinal: isCompleted,
          timestamp: Date.now(),
          letterId: response.result?.letterId,
        };

        onEvent(eventData);

        // If completed, stop polling
        if (isCompleted) {
          isActive = false;
          if (intervalId) clearInterval(intervalId);
        }
      } catch (error) {
        console.error('Task polling error:', error);

        // If the task endpoint fails, it might mean the task is completed
        // Try to detect completion by checking if new letters were created
        try {
          // This is a fallback - don't show errors for this
          const errorResponse = (await (error as any).response?.json?.()) || {};
          if (errorResponse.statusCode === 500) {
            // Task might be completed but endpoint is failing
            // Emit a generic completion event after some time
            setTimeout(() => {
              if (isActive) {
                onEvent({
                  taskId,
                  progress: 100,
                  message:
                    'Letter generation may be complete. Please refresh if needed.',
                  status: 'completed',
                  isFinal: true,
                  timestamp: Date.now(),
                });
                isActive = false;
                if (intervalId) clearInterval(intervalId);
              }
            }, 10000); // Wait 10 seconds before giving up
          }
        } catch (fallbackError) {
          // Ignore fallback errors
        }

        if (isActive) {
          onError?.(error as any);
        }
      }
    };

    // Initial poll
    onEvent({
      taskId,
      message: 'Checking task status...',
      progress: 0,
      status: 'connecting',
      isFinal: false,
      timestamp: Date.now(),
    });

    pollTaskStatus();

    // Poll every 8 seconds to reduce load
    intervalId = setInterval(pollTaskStatus, 8000);

    return () => {
      isActive = false;
      if (intervalId) clearInterval(intervalId);
    };
  },

  async searchInDocuments(payload: {
    case_id: string;
    query: string;
    search_type?: 'fuzzy' | 'exact';
    max_results?: number;
  }): Promise<{
    query: string;
    total_results: number;
    results: Array<{
      fileName: string;
      content: string;
      relevance: number;
      source: string;
      documentId: string;
    }>;
  }> {
    const res = await apiClient.post<any>(`/letters/search/in-documents`, {
      case_id: payload.case_id,
      query: payload.query,
      search_type: payload.search_type || 'fuzzy',
      max_results: payload.max_results ?? 8,
    });
    return res;
  },

  async regenerateLetterPartly(payload: {
    letter_id: string;
    user_prompt: string;
    part_of_letter: string;
    caseId?: string;
    context?: { beforeText?: string; afterText?: string };
  }): Promise<{
    regenerated_content: string;
    letter_id: string;
    updated_at: string;
  }> {
    const res = await apiClient.post<any>(
      `/letters/search/regenerate_letter_partly`,
      {
        letter_id: payload.letter_id,
        user_prompt: payload.user_prompt,
        part_of_letter: payload.part_of_letter,
        caseId: payload.caseId,
        context: payload.context || {},
      }
    );
    return res;
  },
  async extractSources(
    letterId: string,
    options?: { text?: string }
  ): Promise<{
    letterId: string;
    claims: Array<{
      text: string;
      type: string;
      start: number;
      end: number;
      normalized?: Record<string, unknown>;
      sources: Array<Record<string, unknown>>;
    }>;
  }> {
    const res = await apiClient.post<{
      letterId: string;
      claims: Array<{
        text: string;
        type: string;
        start: number;
        end: number;
        normalized?: Record<string, unknown>;
        sources: Array<Record<string, unknown>>;
      }>;
    }>(
      `/letters/${encodeURIComponent(letterId)}/sources/extract`,
      {
        text: options?.text,
      }
    );
    return res;
  },

  // Multi-document upload for letter generation
  async uploadLetterDocument(
    letterId: string,
    file: File,
    documentType: string,
    caseId?: string
  ): Promise<{
    success: boolean;
    documentId: string;
    fileName: string;
    documentType: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    params.append('documentType', documentType);
    if (caseId) {
      params.append('caseId', caseId);
    }

    const res = await apiClient.post<any>(
      `/letters/${encodeURIComponent(letterId)}/documents/upload?${params.toString()}`,
      formData
    );
    return res;
  },

  async getLetterDocuments(letterId: string): Promise<{
    success: boolean;
    documents: Array<{
      id: string;
      documentId: string;
      documentType: string;
      sortOrder: number;
      notes: string | null;
      addedAt: string;
      file: {
        id: string;
        fileName: string;
        fileSize: number | null;
        mimeType: string | null;
        processingStatus: string;
        summary: string | null;
        extractedText: string | null;
        createdAt: string;
      };
    }>;
  }> {
    const res = await apiClient.get<any>(
      `/letters/${encodeURIComponent(letterId)}/documents`
    );
    return res;
  },

  async removeLetterDocument(
    letterId: string,
    letterDocumentId: string
  ): Promise<{
    success: boolean;
    deleted: string;
  }> {
    const res = await apiClient.delete<any>(
      `/letters/${encodeURIComponent(letterId)}/documents/${encodeURIComponent(letterDocumentId)}`
    );
    return res;
  },

  // Referee CV upload endpoints (legacy support)
  async uploadRefereeCVForCase(
    caseId: string,
    file: File
  ): Promise<{
    success: boolean;
    documentId: string;
    fileName: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await apiClient.post<any>(
      `/letters/cases/${encodeURIComponent(caseId)}/referee-upload`,
      formData
    );
    return res;
  },

  async uploadRefereeCVForLetter(
    letterId: string,
    file: File,
    caseId?: string
  ): Promise<{
    success: boolean;
    documentId: string;
    fileName: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const url = caseId
      ? `/letters/${encodeURIComponent(letterId)}/referee-upload?caseId=${encodeURIComponent(caseId)}`
      : `/letters/${encodeURIComponent(letterId)}/referee-upload`;

    const res = await apiClient.post<any>(url, formData);
    return res;
  },

  async getRefereeFile(letterId: string, caseId?: string): Promise<Blob> {
    const url = caseId
      ? `/letters/${encodeURIComponent(letterId)}/referee-file?caseId=${encodeURIComponent(caseId)}`
      : `/letters/${encodeURIComponent(letterId)}/referee-file`;

    return await apiClient.getBlob(url);
  },

  async updateLetter(
    letterId: string,
    updates: {
      content?: string;
      markdownContent?: string;
      status?: string;
    }
  ): Promise<{
    success: boolean;
    letter: LetterRecord;
  }> {
    const res = await apiClient.put<any>(
      `/letters/${encodeURIComponent(letterId)}`,
      updates
    );
    return res;
  },

  /**
   * Batch generate multiple letters with different recommenders
   */
  async createBatchLetters(payload: {
    project_id: string;
    letters: BatchLetterConfig[];
    common_file_ids?: string[];
    common_instructions?: string;
    common_whitelist?: string;
    common_blacklist?: string;
  }): Promise<BatchProgress> {
    const res = await apiClient.post<BatchProgress>('/letters/batch', payload);
    return res;
  },

  /**
   * Get batch generation progress
   */
  async getBatchProgress(batchId: string): Promise<BatchProgress> {
    const res = await apiClient.get<BatchProgress>(
      `/letters/batch/${encodeURIComponent(batchId)}`
    );
    return res;
  },

  /**
   * Download batch letters as ZIP file
   */
  async downloadBatchLetters(batchId: string): Promise<Blob> {
    return await apiClient.getBlob(
      `/letters/batch/${encodeURIComponent(batchId)}/download`
    );
  },

  /**
   * Poll batch progress instead of SSE for better reliability
   */
  streamBatchProgress(
    batchId: string,
    onProgress: (progress: BatchProgress) => void,
    onError?: (error: Error) => void
  ): () => void {
    let isActive = true;
    let intervalId: NodeJS.Timeout;

    const pollBatchStatus = async () => {
      if (!isActive) return;

      try {
        const progress = await LettersApi.getBatchProgress(batchId);
        if (!isActive) return;

        onProgress(progress);

        // Stop polling if batch is completed or failed
        if (
          progress.status === 'completed' ||
          progress.status === 'failed' ||
          progress.status === 'partial_failure'
        ) {
          isActive = false;
          if (intervalId) clearInterval(intervalId);
        }
      } catch (error) {
        console.error('Batch polling error:', error);
        if (isActive) {
          onError?.(error instanceof Error ? error : new Error(String(error)));
        }
      }
    };

    // Initial poll
    pollBatchStatus();

    // Poll every 5 seconds
    intervalId = setInterval(pollBatchStatus, 5000);

    return () => {
      isActive = false;
      if (intervalId) clearInterval(intervalId);
    };
  },
};

export default LettersApi;
